import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Send, Users, X } from "lucide-react";

/**
 * Compose + send admin email. 3 modes:
 *   - mode="user", targetId=user_id      → single recipient (no preview step)
 *   - mode="event", targetId=event_id    → paid attendees (recipient picker + preview)
 *   - mode="broadcast"                   → active users (recipient picker + preview)
 * Flow for bulk: compose → pick recipients (checkboxes) → preview → send.
 */
export default function ComposeEmailDialog({ open, onOpenChange, mode, targetId, recipientLabel, recipientCount, testId = "compose-email" }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("compose");  // 'compose' | 'recipients' | 'confirm'
  const [recipients, setRecipients] = useState([]);
  const [excluded, setExcluded] = useState(new Set());
  const [search, setSearch] = useState("");
  const [recipientsLoading, setRecipientsLoading] = useState(false);

  const reset = () => { setSubject(""); setBody(""); setStep("compose"); setBusy(false); setExcluded(new Set()); setSearch(""); setRecipients([]); };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const bulk = mode !== "user";

  // Load recipients when transitioning to the picker step
  useEffect(() => {
    if (!open || step !== "recipients" || !bulk) return;
    setRecipientsLoading(true);
    const url = mode === "event" ? `/admin/email/event/${targetId}/recipients` : "/admin/email/broadcast/recipients";
    api.get(url).then(r => setRecipients(r.data || []))
      .catch(err => toast.error(err?.response?.data?.detail || "Could not load recipients"))
      .finally(() => setRecipientsLoading(false));
  }, [open, step, bulk, mode, targetId]);

  const activeRecipients = recipients.filter(r => !excluded.has(r.user_id));
  const toggleExclude = (user_id) => {
    setExcluded(prev => {
      const next = new Set(prev);
      if (next.has(user_id)) next.delete(user_id);
      else next.add(user_id);
      return next;
    });
  };

  const send = async () => {
    setBusy(true);
    try {
      const endpoint = mode === "user" ? `/admin/email/user/${targetId}`
        : mode === "event" ? `/admin/email/event/${targetId}`
        : "/admin/email/broadcast";
      const payload = { subject, body };
      if (bulk) payload.recipient_user_ids = activeRecipients.map(r => r.user_id);
      const { data } = await api.post(endpoint, payload);
      toast.success(data.async ? `Sending to ${data.count} recipients in background…` : `Sent to ${data.sent_to || `${data.count} recipient(s)`}`);
      close(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    }
    setBusy(false);
  };

  const canSend = subject.length >= 2 && body.length >= 1;
  const filteredRecipients = recipients.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.name || "").toLowerCase().includes(q) || (r.email || "").toLowerCase().includes(q);
  });

  // Simple HTML preview of the branded wrapper
  const previewHtml = `<div style="font-family: ui-sans-serif, system-ui; max-width:560px; margin:0 auto; background:#fdfaf5; padding:24px; border:1px solid #e5e5e5; border-radius:12px;">
    <div style="font-family: Georgia, serif; font-size:22px; font-weight:600; color:#111; margin-bottom:16px;">${subject}</div>
    <div style="font-size:15px; color:#333; line-height:1.6; white-space:pre-wrap;">${body.replace(/</g, "&lt;")}</div>
    <div style="margin-top:28px; padding-top:20px; border-top:1px solid #eee; font-size:12px; color:#888;">— Offline &amp; Available</div>
  </div>`;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            {bulk ? <Users size={18} /> : <Send size={18} />}
            {step === "compose" ? (mode === "user" ? "Email this user" : mode === "event" ? "Email event attendees" : "Email all users") :
             step === "recipients" ? "Choose recipients" : "Preview & send"}
          </DialogTitle>
          <DialogDescription className="text-sm text-neutral-500">
            {step === "compose" ? (
              <>Will send to <span className="font-medium text-black">{recipientLabel}</span>{bulk && recipientCount ? ` (${recipientCount} recipient${recipientCount === 1 ? "" : "s"})` : ""}. From: hello@offlineandavailable.com</>
            ) : step === "recipients" ? (
              <>Uncheck anyone you want to skip. {activeRecipients.length} of {recipients.length} will receive this email.</>
            ) : (
              <>About to email <span className="font-medium text-black">{bulk ? activeRecipients.length : 1} recipient{(bulk ? activeRecipients.length : 1) === 1 ? "" : "s"}</span>. This can't be undone.</>
            )}
          </DialogDescription>
        </DialogHeader>

        {step === "compose" && (
          <div className="space-y-3">
            <div>
              <div className="text-eyebrow mb-1">Subject</div>
              <Input value={subject} onChange={e=>setSubject(e.target.value)} placeholder={mode === "event" ? "Quick reminder about tomorrow" : "A note from Offline & Available"} className="rounded-xl" data-testid={`${testId}-subject`} />
            </div>
            <div>
              <div className="text-eyebrow mb-1">Message</div>
              <Textarea rows={10} value={body} onChange={e=>setBody(e.target.value)} placeholder="Write your message here. Blank lines create new paragraphs — single line breaks become soft breaks." className="rounded-xl" data-testid={`${testId}-body`} />
              <div className="text-[11px] text-neutral-500 mt-1">The email will be wrapped in your brand template automatically.</div>
            </div>
            <Button onClick={()=>setStep(bulk ? "recipients" : "confirm")} disabled={!canSend} className="w-full rounded-full bg-black text-white btn-pill" data-testid={`${testId}-preview-btn`}>
              {bulk ? "Choose recipients →" : "Preview and send →"}
            </Button>
          </div>
        )}

        {step === "recipients" && (
          <div className="space-y-3">
            <Input
              placeholder="Search by name or email…"
              value={search}
              onChange={e=>setSearch(e.target.value)}
              className="rounded-xl"
              data-testid={`${testId}-search`}
            />
            <div className="flex items-center justify-between text-xs">
              <div className="text-neutral-600"><span className="font-medium text-black">{activeRecipients.length}</span> of {recipients.length} selected</div>
              <div className="flex gap-2">
                <button onClick={()=>setExcluded(new Set())} className="underline underline-offset-2 hover:text-black" data-testid={`${testId}-select-all`}>Select all</button>
                <button onClick={()=>setExcluded(new Set(recipients.map(r => r.user_id)))} className="underline underline-offset-2 hover:text-black" data-testid={`${testId}-select-none`}>Clear all</button>
              </div>
            </div>
            <div className="rounded-xl border border-neutral-200 max-h-72 overflow-y-auto" data-testid={`${testId}-recipient-list`}>
              {recipientsLoading ? (
                <div className="p-6 text-center text-sm text-neutral-500">Loading…</div>
              ) : filteredRecipients.length === 0 ? (
                <div className="p-6 text-center text-sm text-neutral-500">{recipients.length === 0 ? "No recipients." : "No match."}</div>
              ) : filteredRecipients.map(r => {
                const skipped = excluded.has(r.user_id);
                return (
                  <label key={r.user_id} className={`flex items-center gap-3 px-4 py-2.5 border-b last:border-b-0 cursor-pointer hover:bg-cream/40 ${skipped ? "opacity-50" : ""}`} data-testid={`${testId}-recipient-${r.user_id}`}>
                    <input
                      type="checkbox"
                      checked={!skipped}
                      onChange={()=>toggleExclude(r.user_id)}
                      className="h-4 w-4 accent-black"
                      data-testid={`${testId}-check-${r.user_id}`}
                    />
                    <div className="flex-1 min-w-0 flex items-baseline justify-between gap-4">
                      <div className="truncate">
                        <div className="text-sm font-medium">{r.name || "—"}</div>
                        <div className="text-xs text-neutral-500 font-mono truncate">{r.email}</div>
                      </div>
                      {skipped && (
                        <button type="button" onClick={(e)=>{ e.preventDefault(); toggleExclude(r.user_id); }} className="text-xs text-rose-600 hover:underline inline-flex items-center gap-1" data-testid={`${testId}-restore-${r.user_id}`}>
                          <X size={10} /> excluded
                        </button>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={()=>setStep("compose")} className="flex-1 rounded-full" data-testid={`${testId}-back-to-compose`}>← Edit message</Button>
              <Button onClick={()=>setStep("confirm")} disabled={activeRecipients.length === 0} className="flex-1 rounded-full bg-black text-white btn-pill" data-testid={`${testId}-to-preview`}>Preview email →</Button>
            </div>
          </div>
        )}

        {step === "confirm" && (
          <div className="space-y-3">
            <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3 text-xs">
              <div className="text-eyebrow mb-1">Sending to</div>
              {bulk ? (
                <div className="text-neutral-700">
                  <span className="font-medium text-black">{activeRecipients.length}</span> recipients
                  {excluded.size > 0 && <span className="text-neutral-500"> · {excluded.size} skipped</span>}
                </div>
              ) : (
                <div className="text-neutral-700 font-mono">{recipientLabel}</div>
              )}
            </div>
            <div className="rounded-xl border border-neutral-200 bg-cream/40 p-4 max-h-96 overflow-y-auto" data-testid={`${testId}-html-preview`}>
              <div className="text-eyebrow mb-2">Email preview</div>
              <div className="rounded-md bg-white p-3" dangerouslySetInnerHTML={{ __html: previewHtml }} />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={()=>setStep(bulk ? "recipients" : "compose")} disabled={busy} className="flex-1 rounded-full" data-testid={`${testId}-back-btn`}>← Back</Button>
              <Button onClick={send} disabled={busy || (bulk && activeRecipients.length === 0)} className="flex-1 rounded-full bg-black text-white btn-pill" data-testid={`${testId}-send-btn`}>
                {busy ? "Sending…" : `Send to ${bulk ? activeRecipients.length : 1}`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
